<!-- WBS Deviation Report — layout aligned with project management dashboards -->
<div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 deviation-report-container">

  <!-- Header -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">WBS Deviation Report</h1>
        <p class="text-gray-600">Compare planned vs actual dates and progress</p>
      </div>
      <div class="flex flex-wrap gap-3">
        <app-back-button
          label="Back to WBS"
          [link]="backLink">
        </app-back-button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <div>
        <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Project</label>
        <select
          [(ngModel)]="selectedProject"
          (change)="onFilterChange()"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
        >
          <option value="">All Projects</option>
          <option *ngFor="let project of projects" [value]="project.id">
            {{ project.project_name }}<ng-container *ngIf="project.project_identifier"> ({{ project.project_identifier }})</ng-container>
          </option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Start Date</label>
        <input
          type="date"
          [value]="startDate"
          (input)="startDate = $any($event.target).value; onFilterChange()"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
        />
      </div>
      <div>
        <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">End Date</label>
        <input
          type="date"
          [value]="endDate"
          (input)="endDate = $any($event.target).value; onFilterChange()"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
        />
      </div>
      <div>
        <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Group By</label>
        <select
          [(ngModel)]="groupBy"
          (change)="onGroupByChange()"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
        >
          <option value="activity">By Activity</option>
          <option value="project">By Project</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Summary -->
  <div *ngIf="reportData?.summary" class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Summary</h2>
      <p class="text-sm text-gray-600 mt-1">High-level metrics for activities in scope</p>
    </div>
    <div class="p-6">
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <div class="summary-tile">
          <div class="summary-label">Total Activities</div>
          <div class="summary-value">{{ reportData.summary.total_activities }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Total Projects</div>
          <div class="summary-value">{{ reportData.summary.total_projects }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Delayed Start</div>
          <div class="summary-value negative">{{ reportData.summary.activities_delayed_start }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Early Start</div>
          <div class="summary-value positive">{{ reportData.summary.activities_early_start }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">On Time Start</div>
          <div class="summary-value neutral">{{ reportData.summary.activities_on_time_start }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Delayed End</div>
          <div class="summary-value negative">{{ reportData.summary.activities_delayed_end }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Early End</div>
          <div class="summary-value positive">{{ reportData.summary.activities_early_end }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">On Time End</div>
          <div class="summary-value neutral">{{ reportData.summary.activities_on_time_end }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Behind Schedule</div>
          <div class="summary-value negative">{{ reportData.summary.activities_behind_schedule }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Ahead Schedule</div>
          <div class="summary-value positive">{{ reportData.summary.activities_ahead_schedule }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">On Track</div>
          <div class="summary-value neutral">{{ reportData.summary.activities_on_track }}</div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Avg Start Deviation</div>
          <div class="summary-value" [ngClass]="getDeviationClass(reportData.summary.avg_start_date_deviation)">
            {{ reportData.summary.avg_start_date_deviation?.toFixed(1) || 0 }} days
          </div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Avg End Deviation</div>
          <div class="summary-value" [ngClass]="getDeviationClass(reportData.summary.avg_end_date_deviation)">
            {{ reportData.summary.avg_end_date_deviation?.toFixed(1) || 0 }} days
          </div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Avg Duration Deviation</div>
          <div class="summary-value" [ngClass]="getDeviationClass(reportData.summary.avg_duration_deviation)">
            {{ reportData.summary.avg_duration_deviation?.toFixed(1) || 0 }} days
          </div>
        </div>
        <div class="summary-tile">
          <div class="summary-label">Avg Progress Deviation</div>
          <div class="summary-value" [ngClass]="getProgressDeviationClass(reportData.summary.avg_progress_deviation)">
            {{ reportData.summary.avg_progress_deviation?.toFixed(1) || 0 }}%
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-lg border border-gray-200">
    <mat-spinner diameter="48"></mat-spinner>
    <p class="text-gray-600 mt-4">Generating report...</p>
  </div>

  <!-- Deviation table -->
  <div *ngIf="!loading && reportData" class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Deviation Details</h2>
      <p class="text-sm text-gray-600 mt-1">Activity-level comparison of plan vs actual</p>
    </div>

    <div class="table-wrapper">
      <table class="deviation-table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Project</th>
            <th>Plan Start</th>
            <th>Actual Start</th>
            <th>Start Dev.</th>
            <th>Plan End</th>
            <th>Actual End</th>
            <th>End Dev.</th>
            <th>Planned Duration</th>
            <th>Actual Duration</th>
            <th>Duration Dev.</th>
            <th>% Complete</th>
            <th>Expected %</th>
            <th>Progress Dev.</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of reports" [ngClass]="getRowHighlightClass(item)">
            <td>
              <strong>{{ item.activity_name }}</strong>
              <br />
              <small class="text-muted" *ngIf="item.activity_code">{{ item.activity_code }}</small>
            </td>
            <td>
              {{ item.project_name }}
              <br />
              <small class="text-muted" *ngIf="item.project_identifier">{{ item.project_identifier }}</small>
            </td>
            <td>{{ formatDate(item.plan_start_date) }}</td>
            <td>{{ formatDate(item.actual_start_date) }}</td>
            <td>
              <span [ngClass]="getDeviationClass(item.start_date_deviation, item.start_date_status)">
                <mat-icon class="icon-small">{{ getDeviationIcon(item.start_date_status) }}</mat-icon>
                {{ item.start_date_deviation !== null && item.start_date_deviation !== undefined
                  ? (item.start_date_deviation > 0 ? '+' : '') + item.start_date_deviation + 'd'
                  : '-' }}
              </span>
            </td>
            <td>{{ formatDate(item.plan_end_date) }}</td>
            <td>{{ formatDate(item.actual_end_date) }}</td>
            <td>
              <span [ngClass]="getDeviationClass(item.end_date_deviation, item.end_date_status)">
                <mat-icon class="icon-small">{{ getDeviationIcon(item.end_date_status) }}</mat-icon>
                {{ item.end_date_deviation !== null && item.end_date_deviation !== undefined
                  ? (item.end_date_deviation > 0 ? '+' : '') + item.end_date_deviation + 'd'
                  : '-' }}
              </span>
            </td>
            <td>{{ item.planned_duration || '-' }} days</td>
            <td>{{ item.actual_duration || '-' }} days</td>
            <td>
              <span [ngClass]="getDeviationClass(item.duration_deviation, item.duration_status)">
                {{ item.duration_deviation !== null && item.duration_deviation !== undefined
                  ? (item.duration_deviation > 0 ? '+' : '') + item.duration_deviation + 'd'
                  : '-' }}
              </span>
            </td>
            <td>
              <div class="progress-bar-container">
                <div class="progress-bar" [style.width.%]="item.percent_complete"></div>
                <span class="progress-text">{{ item.percent_complete }}%</span>
              </div>
            </td>
            <td>{{ item.expected_percent_complete !== null && item.expected_percent_complete !== undefined ? item.expected_percent_complete + '%' : '-' }}</td>
            <td>
              <span [ngClass]="getProgressDeviationClass(item.progress_deviation, item.progress_status)">
                {{ item.progress_deviation !== null && item.progress_deviation !== undefined
                  ? (item.progress_deviation > 0 ? '+' : '') + item.progress_deviation.toFixed(1) + '%'
                  : '-' }}
              </span>
            </td>
            <td>
              <span class="badge" [ngClass]="getStatusBadgeClass(item.progress_status)">
                {{ getStatusText(item.progress_status) }}
              </span>
            </td>
          </tr>
          <tr *ngIf="reports.length === 0">
            <td colspan="15" class="text-center py-12 text-gray-500">No data available</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
